<template>
	<div class="aidecision" :class="{ 'aidecision--running': isRunning }">
		<div class="aidecision__action">
			<ButtonFlat variant="white" :disabled="disabled" :title="disabledReason" @buttonclick="$emit('generate')">
				<template #icon><AiSparkIcon :color="'#2468c1'" /></template>
				{{ texts.aiDecision.generate }}
			</ButtonFlat>
		</div>

		<p class="aidecision__hint"><strong>{{ texts.aiDecision.hintLabel }}</strong> {{ texts.aiDecision.hint }}</p>

		<div v-if="isRunning" class="aidecision__progress">
			<AiSparkIcon :color="'#ffffff'" />
			<span class="aidecision__timer">{{ elapsedLabel }}</span>
			<span class="aidecision__step">{{ stepLabel }} ...</span>
		</div>

		<p v-else-if="errorMessage" class="aidecision__error">{{ errorMessage }}</p>

		<p v-else-if="disabled && disabledReason" class="aidecision__disabledhint">{{ disabledReason }}</p>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ButtonFlat from '../buttons/ButtonFlat.vue';
import AiSparkIcon from '@/assets/icons/AiSparkIcon.vue';
import { texts } from '@/utils/texts';
import { AiFailureCategory, AiJobResponse, AiJobStatus, AiJobStep } from '@/types/types';

export default defineComponent({
	name: 'BoxAiDecision',
	components: {
		ButtonFlat,
		AiSparkIcon,
	},
	props: {
		job: {
			type: Object as PropType<AiJobResponse | null>,
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		/** Grund der Sperre — als Tooltip am Button und als Text im Panel, damit die Sperre erklaert ist. */
		disabledReason: {
			type: String,
			default: '',
		},
	},
	emits: ['generate'],
	data() {
		return {
			now: Date.now(),
			ticker: null as ReturnType<typeof setInterval> | null,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		isRunning(): boolean {
			return this.job?.status === AiJobStatus.Running;
		},
		stepLabel(): string {
			const step = this.job?.step ?? AiJobStep.Collecting;
			return texts.aiDecision.steps[step] ?? '';
		},
		/**
		 * Laufzeit aus der Serverzeit des Starts, nicht aus dem Klickzeitpunkt — sonst zeigt die
		 * Anzeige nach Reload oder Re-Attach eine falsche Dauer.
		 */
		elapsedLabel(): string {
			if (!this.job) return '';
			const seconds = Math.max(0, Math.floor((this.now - new Date(this.job.startedAt).getTime()) / 1000));
			if (seconds < 60) return `${seconds}s`;
			return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
		},
		/**
		 * Je Abbruchgrund ein eigener Satz. Eine Sammelmeldung fuer alle fehlenden Eingaben waere in
		 * jedem konkreten Fall zur Haelfte falsch und schickt die Geschaeftsstelle auf die falsche Suche.
		 */
		errorMessage(): string {
			if (this.job?.status !== AiJobStatus.Failed) return '';
			const failure = this.job.failure;
			switch (failure?.category) {
				case AiFailureCategory.MissingReasonTexts:
					return texts.aiDecision.missingReasonTexts;
				case AiFailureCategory.MissingMediaDescriptions:
					// Ohne benanntes Medium fehlt nicht die Beschreibung, sondern das Medium selbst
					return failure.mediaWithoutDescription.length === 0
						? texts.aiDecision.missingMediaNoMedia
						: texts.aiDecision.missingMediaDescriptions(failure.mediaWithoutDescription, failure.mediaTotal);
				case AiFailureCategory.GenerationNotPossible:
					return texts.aiDecision.generationNotPossible;
				case AiFailureCategory.InsufficientInput:
					return texts.aiDecision.insufficientInput;
				default:
					return texts.aiDecision.internalError;
			}
		},
	},
	methods: {
		startTicker() {
			if (this.ticker) return;
			this.ticker = setInterval(() => {
				this.now = Date.now();
			}, 1000);
		},
		stopTicker() {
			if (!this.ticker) return;
			clearInterval(this.ticker);
			this.ticker = null;
		},
	},
	watch: {
		isRunning: {
			immediate: true,
			handler(running: boolean) {
				// Der Timer stoppt im Endzustand und bleibt mit der Gesamtdauer stehen
				if (running) this.startTicker();
				else this.stopTicker();
			},
		},
	},
	mounted() {
		this.now = Date.now();
	},
	unmounted() {
		this.stopTicker();
	},
});
</script>

<style lang="scss" scoped>
@use '../../scss/vars.scss' as *;

/* Masse aus Figma-Node 67:475 */
.aidecision {
	padding: 32px 24px;
	border: 1px solid #b5cbf4;
	border-radius: 10px;
	background-color: var(--el-color-ai);
	text-align: center;
}

/* Waehrend der Generierung wandert der Glanz ueber die ganze Flaeche, nicht nur ueber den Button */
.aidecision--running {
	background-image: linear-gradient(
		90deg,
		var(--el-color-ai) 0%,
		var(--el-color-ai) 30%,
		var(--el-color-ai-shine) 50%,
		var(--el-color-ai) 70%,
		var(--el-color-ai) 100%
	);
	background-size: 250% 100%;
	animation: aiPanelShine 2.4s linear infinite;
}

@keyframes aiPanelShine {
	from {
		background-position: 150% 0;
	}
	to {
		background-position: -50% 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.aidecision--running {
		animation: none;
	}
}

.aidecision__action {
	display: flex;
	justify-content: center;
}

.aidecision__hint {
	max-width: 855px;
	margin: 16px auto 0;
	font-size: 14px;
	line-height: 1.3;
	color: #fff;
}

.aidecision__progress {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 24px;
	font-size: 14px;
	font-weight: 500;
	color: #fff;
}

.aidecision__timer {
	font-variant-numeric: tabular-nums;
}

.aidecision__error,
.aidecision__disabledhint {
	max-width: 855px;
	margin: 24px auto 0;
	padding: 8px 12px;
	border-radius: 6px;
	background-color: rgba(255, 255, 255, 0.15);
	font-size: 14px;
	line-height: 1.3;
	color: #fff;
}

/* mobile styles */
@media screen and (max-width: $mobile_maxwidth) {
	.aidecision {
		padding: 24px 16px;
	}
}
</style>
